import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  X, 
  FileText, 
  Image as ImageIcon,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2
} from 'lucide-react';
import { emendaService } from '../services/emendaService';
import { Emenda, EmendaDocumento } from '../types/emenda';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../lib/utils';

export const EmendaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [formData, setFormData] = useState<Omit<Emenda, 'id' | 'created_at' | 'documentos'>>({
    titulo: '',
    resumo: '',
    descricao_completa: '',
    valor: 0,
    origem_verba: '',
    area_investimento: '',
    municipio: '',
    estado: 'SP',
    data_publicacao: new Date().toISOString().split('T')[0],
    imagem_url: '',
  });

  const [existingDocuments, setExistingDocuments] = useState<EmendaDocumento[]>([]);
  const [newDocuments, setNewDocuments] = useState<{ file: File, nome: string }[]>([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      fetchEmenda(id);
    }
  }, [isEditing, id]);

  const fetchEmenda = async (id: string) => {
    try {
      const data = await emendaService.getById(id);
      setFormData({
        titulo: data.titulo,
        resumo: data.resumo,
        descricao_completa: data.descricao_completa,
        valor: data.valor,
        origem_verba: data.origem_verba,
        area_investimento: data.area_investimento,
        municipio: data.municipio || (data as any).municipality || '',
        estado: data.estado,
        data_publicacao: data.data_publicacao.split('T')[0],
        imagem_url: data.imagem_url || '',
      });
      setExistingDocuments(data.documentos || []);
    } catch (err) {
      setMsg({ type: 'error', text: 'Não foi possível carregar os dados da emenda.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'valor' ? Number(value) : value 
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setImageFile(file);
  };

  const handleNewDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const addedDocs = Array.from(files).map(file => ({
      file,
      nome: file.name.split('.').slice(0, -1).join('.') || file.name
    }));

    setNewDocuments(prev => [...prev, ...addedDocs]);
    if (e.target) e.target.value = '';
  };

  const removeNewDocField = (idx: number) => {
    setNewDocuments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveExistingDoc = (docId: string) => {
    setRemovedDocumentIds(prev => [...prev, docId]);
    setExistingDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);

    try {
      let finalImageUrl = formData.imagem_url;

      if (imageFile) {
        finalImageUrl = await emendaService.uploadImage(imageFile);
      }

      let emendaId = id;
      const dataToSave = { ...formData, imagem_url: finalImageUrl };

      if (isEditing && id) {
        await emendaService.update(id, dataToSave);
      } else {
        const newEmenda = await emendaService.create(dataToSave);
        emendaId = newEmenda.id;
      }

      for (const docId of removedDocumentIds) {
        await emendaService.removeDocument(docId, '');
      }

      for (const doc of newDocuments) {
        if (doc.file && doc.nome && emendaId) {
          const fileUrl = await emendaService.uploadDocument(doc.file);
          await emendaService.addDocument(emendaId, doc.nome, fileUrl);
        }
      }

      setMsg({ type: 'success', text: `Emenda ${isEditing ? 'atualizada' : 'criada'} com sucesso!` });
      setTimeout(() => navigate('/admin/emendas'), 1500);

    } catch (err: any) {
      console.error(err);
      setMsg({ type: 'error', text: err.message || 'Erro ao salvar os dados.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-slate-400">
        <Loader2 className="animate-spin" size={48} />
        <p className="font-bold tracking-widest uppercase text-xs">Carregando Dados...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-20 px-4">
      {/* Header */}
      <div className="flex items-center gap-6 mb-10">
        <button 
          onClick={() => navigate('/admin/emendas')}
          className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-500 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm active:scale-95"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            {isEditing ? 'Editar Emenda' : 'Nova Emenda Parlamentar'}
          </h1>
          <p className="text-slate-500 font-medium">Gerencie credenciais e documentos da emenda.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <AnimatePresence mode="wait">
          {msg && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={cn(
                "p-4 rounded-2xl flex items-center gap-3 font-bold text-sm",
                msg.type === 'success' ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-red-50 text-red-600 border border-red-100"
              )}
            >
              {msg.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
              {msg.text}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center"><FileText size={18} /></div>
                <h3 className="text-lg font-black text-slate-900">Dados da Emenda</h3>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Título</label>
                  <input 
                    required name="titulo" value={formData.titulo} onChange={handleChange}
                    className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-blue-500/10 transition-all outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Resumo</label>
                  <input 
                    required name="resumo" value={formData.resumo} onChange={handleChange}
                    className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-blue-500/10 transition-all outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Descrição Completa</label>
                  <textarea 
                    required name="descricao_completa" value={formData.descricao_completa} onChange={handleChange} rows={6}
                    className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-blue-500/10 transition-all outline-none resize-none"
                  />
                </div>
              </div>
            </section>

            <section className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center"><CheckCircle2 size={18} /></div>
                <h3 className="text-lg font-black text-slate-900">Financeiro e Localização</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Valor (R$)</label>
                  <input type="number" step="0.01" name="valor" value={formData.valor} onChange={handleChange} className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl font-black text-slate-900 outline-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Data Publicação</label>
                  <input type="date" name="data_publicacao" value={formData.data_publicacao} onChange={handleChange} className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-900 outline-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Origem</label>
                  <input name="origem_verba" value={formData.origem_verba} onChange={handleChange} className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-900 outline-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Área</label>
                  <select name="area_investimento" value={formData.area_investimento} onChange={handleChange} className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-900 outline-none appearance-none">
                    <option value="">Selecione</option>
                    <option value="Educação">Educação</option>
                    <option value="Saúde">Saúde</option>
                    <option value="Infraestrutura">Infraestrutura</option>
                    <option value="Esporte">Esporte</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Município</label>
                  <input name="municipio" value={formData.municipio} onChange={handleChange} className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-900 outline-none" />
                </div>
              </div>
            </section>
          </div>

          <div className="space-y-8">
            <section className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center"><ImageIcon size={18} /></div>
                <h3 className="text-lg font-black text-slate-900">Imagem Destaque</h3>
              </div>
              <div className="relative aspect-video rounded-3xl bg-slate-50 border-2 border-dashed border-slate-200 overflow-hidden flex flex-col items-center justify-center gap-2 group cursor-pointer">
                {imageFile ? (
                   <img src={URL.createObjectURL(imageFile)} className="w-full h-full object-cover" alt="preview" />
                ) : formData.imagem_url ? (
                   <img src={formData.imagem_url} className="w-full h-full object-cover" alt="current" />
                ) : (
                  <Upload size={32} className="text-slate-300" />
                )}
                <input type="file" accept="image/*" onChange={handleImageChange} className="absolute inset-0 opacity-0 cursor-pointer" title="Selecionar imagem" />
              </div>
            </section>

            <section className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-violet-50 text-violet-600 rounded-lg flex items-center justify-center"><FileText size={18} /></div>
                  <h3 className="text-lg font-black text-slate-900">Documentos PDF</h3>
                </div>
                <label className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer">
                  <Plus size={20} />
                  <input type="file" multiple accept=".pdf" className="hidden" onChange={handleNewDocFileChange} />
                </label>
              </div>

              <div className="space-y-3">
                {existingDocuments.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                    <div className="w-10 h-10 bg-white text-emerald-500 rounded-xl flex items-center justify-center shadow-sm"><CheckCircle2 size={20} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Anexado</p>
                      <p className="text-xs font-bold text-slate-700 truncate">{doc.nome}</p>
                    </div>
                    <button type="button" onClick={() => handleRemoveExistingDoc(doc.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                ))}
                
                {newDocuments.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-2xl">
                    <div className="w-10 h-10 bg-white text-blue-600 rounded-xl flex items-center justify-center shadow-sm"><FileText size={20} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest">Aguardando Salvar</p>
                      <p className="text-xs font-bold text-slate-700 truncate">{doc.file.name}</p>
                    </div>
                    <button type="button" onClick={() => removeNewDocField(idx)} className="text-blue-300 hover:text-red-500"><X size={16} /></button>
                  </div>
                ))}

                {!existingDocuments.length && !newDocuments.length && (
                  <div className="py-10 text-center bg-slate-50/50 border-2 border-dashed border-slate-100 rounded-[2rem]">
                    <Upload className="mx-auto text-slate-200 mb-2" size={32} />
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Nenhum PDF</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <div className="sticky bottom-8 bg-white/80 backdrop-blur-md p-6 rounded-[2.5rem] border border-slate-200 shadow-xl flex items-center justify-between z-20">
          <button type="button" onClick={() => navigate('/admin/emendas')} className="text-slate-400 font-black text-xs uppercase tracking-widest hover:text-slate-900 transition-colors">Voltar</button>
          <button type="submit" disabled={saving} className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-3 hover:bg-blue-700 shadow-xl shadow-blue-600/20 transition-all active:scale-95 disabled:opacity-50">
            {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
            {saving ? 'Salvando...' : 'Salvar Emenda'}
          </button>
        </div>
      </form>
    </div>
  );
};
